import type { LogValue } from "../types";

/**
 * Keys whose values must never be logged verbatim, matched case-insensitively as a whole
 * word (not a substring — `tokenizer`/`passwordless` must not match). camelCase keys carry
 * no `^`/`$`/`_`/`-` boundary, so `isSecretKey` first splits an acronym run, then a
 * lowercase/digit-before-uppercase boundary; reversing the order re-merges what the acronym split produced (`APISecret` → `API_Secret` → `api_Secret`).
 */
const SECRET_KEY_PATTERN =
	/(?:^|_|-)(?:token|secret|password|api[_-]?key|authorization|credential|cookie|secure[_-]pin)(?:_|-|$)/i;

/** See {@link SECRET_KEY_PATTERN}'s BOUNDARY RULE for the camelCase → snake_case steps. */
function isSecretKey(key: string): boolean {
	const withAcronymBoundary = key.replace(/([A-Z]+)([A-Z][a-z])/g, "$1_$2");
	const snakeCased = withAcronymBoundary.replace(/([a-z0-9])([A-Z])/g, "$1_$2");
	return SECRET_KEY_PATTERN.test(snakeCased);
}

const REDACTED = "[redacted]";
const CIRCULAR = "[circular]";
const MAX_DEPTH = 12;

function isPlainRecord(value: object): value is Record<string, unknown> {
	const proto = Object.getPrototypeOf(value);
	return proto === Object.prototype || proto === null;
}

/**
 * Convert an arbitrary input into a closed `LogValue`: primitives pass through
 * (`bigint`/`symbol`/`function` tagged as strings), `Error` is preserved, `Date` is
 * serialized to ISO, arrays/plain objects recurse redacting secret-named keys, and
 * circular/over-deep/non-plain objects are tagged, so the result always serializes.
 */
export function normalizeLogValue(input: unknown, seen: WeakSet<object> = new WeakSet(), depth = 0): LogValue {
	if (typeof input === "string" || typeof input === "number" || typeof input === "boolean") {
		return input;
	}
	if (input === null) {
		return null;
	}
	if (input === undefined) {
		return undefined;
	}
	if (typeof input === "bigint") {
		return `${input.toString()}n`;
	}
	if (typeof input === "function") {
		return "[function]";
	}
	if (typeof input === "symbol") {
		return input.toString();
	}
	if (input instanceof Error) {
		return input;
	}
	if (input instanceof Date) {
		return input.toISOString();
	}
	if (typeof input === "object") {
		if (seen.has(input)) {
			return CIRCULAR;
		}
		if (depth >= MAX_DEPTH) {
			return "[max-depth]";
		}
		seen.add(input);
		const result = Array.isArray(input)
			? input.map((item) => normalizeLogValue(item, seen, depth + 1))
			: normalizeRecord(input, seen, depth);
		seen.delete(input);
		return result;
	}
	return String(input);
}

function normalizeRecord(input: object, seen: WeakSet<object>, depth: number): LogValue {
	if (!isPlainRecord(input)) {
		return `[${input.constructor?.name ?? "object"}]`;
	}
	const output: Record<string, LogValue> = {};
	for (const [key, value] of Object.entries(input)) {
		output[key] = isSecretKey(key) ? REDACTED : normalizeLogValue(value, seen, depth + 1);
	}
	return output;
}
